import { useMemo } from 'react'
import { useEventHandler, contains } from '@zambezi/sdk/common'
import keycode from 'keycode'
import moment from './moment'

/**
 * @param {string|number|Date=} date
 * @returns {number}
 */
const toDayCount = date => moment(date).diff(0, 'days')
/**
 * @readonly
 * @type {Record<string, number>}
 */
const navigations = {
  left: 1,
  right: 1,
  up: 1,
  down: 1,
  enter: 1,
  esc: 1,
  home: 1,
  end: 1,
  'page up': 1,
  'page down': 1
}

const supportedModifierKeys = {
  Shift: 1
}

export const FORMAT = 'DD-MMM-YYYY'

/**
 * @param {moment.ConfigType=} date
 * @returns {Date}
 */
export function useStartOfDay (date) {
  return useMemo(
    () =>
      moment(date)
        .startOf('day')
        .toDate(),
    [date]
  )
}

/**
 * @param {string[]|number[]|Date[]|moment.Dayjs[]} disabledDates
 * @returns {(date?:moment.ConfigType) => boolean}
 */
export function disableDayList (disabledDates) {
  const dayCountList = disabledDates.map(toDayCount)
  /** @type {(date?:moment.ConfigType) => boolean} */
  return date => {
    const dayCount = toDayCount(date)
    return dayCountList.some(dc => dayCount === dc)
  }
}

/**
 * @param {import('react').RefObject<(keyCode: string, inside: boolean, modifierKeys: string?) => void>} callbackRef
 * @param {import('react').RefObject<Element>} elementRef
 */
export function useNavigation (callbackRef, elementRef) {
  useEventHandler('keydown', onDocumentKeydown)
  useEventHandler('mousedown', onMouseDown)

  /**
   * @type {EventListener}
   */
  function onDocumentKeydown (event) {
    const keyCode = keycode(event)
    const modifierKeys = getModifierKeys(event)
    const { current: callback } = callbackRef
    if (callback && navigations[keyCode]) {
      const inside = contains(elementRef.current, event.target)
      if (!inside) {
        event.preventDefault()
      }
      callback(keyCode, inside, modifierKeys)
    }
  }

  /**
   * @type {EventListener}
   */
  function onMouseDown (event) {
    if (contains(elementRef.current, event.target)) {
      event.preventDefault()
    }
  }

  /**
   * @param {import('react').SyntheticEvent} event
   * @returns {string[]}
   */
  function getModifierKeys (event) {
    const modifierKeys = []
    for (let key in supportedModifierKeys) {
      if (event.getModifierState(key)) {
        modifierKeys.push(key)
      }
    }
    return modifierKeys
  }
}
